import router from '@/router';
import { getId } from '@/assets/js/utils';
import DocumentType from '@/assets/js/enum/DocumentType';
import DocumentStatus from '@/assets/js/enum/DocumentStatus';
import RoundStatus from '@/assets/js/enum/RoundStatus';
import { xhr } from '@/xhr/endpoints';
import { counterpointModesMap } from '@/assets/js/constants';
import { getOutlineNodeForDisplay } from '../../assets/js/utils';
import cookies from '@/assets/js/cookies';
import {
  toKeyedObj,
  prepCounterpoints,
  prepNodes,
  matchAnnotationsToNode,
} from '@/utils/documentHelpers';

//
// Helper functions for building cached computed data
const buildAllData = getters => {
  const sourceAnalytics = getters.documentSourceAnalytics;
  const nodes = getters.documentSourceTextNodes;

  // Safety checks
  if (!sourceAnalytics || !nodes || !Array.isArray(nodes)) {
    return {};
  }

  const documentData = {};

  Object.keys(sourceAnalytics).forEach(sourceType => {
    let headgingData = sourceAnalytics[sourceType];
    let basisNodeKeys =
      headgingData?.nodes?.flatMap(node => node.basisNodes.map(basisNode => basisNode.nodeKey)) ||
      [];
    const basisSet = new Set(basisNodeKeys);
    const documentNodeData = {};
    let firstBasisNodeStarts =
      headgingData.nodes?.map(node =>
        parseInt(node.basisNodes[0].nodeKey.split('-')[0].slice(1))
      ) || [];
    let lastBasisNodeEnds =
      headgingData.nodes?.map(node =>
        parseInt(node.basisNodes[node.basisNodes.length - 1].nodeKey.split('-')[1])
      ) || [];

    nodes?.forEach(spanNode => {
      let spanNodeStart = parseInt(spanNode.nodeKey.split('-')[0].slice(1));
      let spanNodeEnd = parseInt(spanNode.nodeKey.split('-')[1]);

      let data =
        basisSet.has(spanNode.nodeKey) &&
        firstBasisNodeStarts?.some(start => spanNodeStart >= start) &&
        lastBasisNodeEnds?.some(end => spanNodeEnd <= end);

      documentNodeData[spanNode?.nodeKey] = data;
    });

    documentData[sourceType] = documentNodeData;
  });
  return documentData;
};

const buildNodeAnnotations = (state, getters) => {
  const prepared = prepCounterpoints(state.counterpointData?.rounds);
  const nodes = prepNodes(getters.documentSourceTextNodes);

  // Safety checks
  if (!prepared || !nodes || !Array.isArray(nodes) || !Array.isArray(prepared.counterpoints)) {
    return {};
  }

  const result = Object.create(null);

  for (const node of nodes) {
    const { annIDs, nodeCPs } = matchAnnotationsToNode(
      node,
      prepared.counterpoints,
      prepared.spatialIndex
    );

    result[node.nodeKey] = {
      counterpointAnnotationIDs: annIDs,
      counterpoints: nodeCPs,
    };
  }
  return result;
};

const buildLLMNodeAnnotations = (state, getters) => {
  const counterpoints = state.counterpointData?.rounds;
  if (!getters.documentOutlineSource || getters.documentOutlineHumanSource?.outline) {
    return false;
  }

  // Add null checks for outline data
  const outlineSource = getters.documentOutlineSource;
  if (!outlineSource?.outline) {
    return false;
  }

  // ***
  const nodes = outlineSource.outline?.outline
    ? outlineSource.outline.outline.map(node => getOutlineNodeForDisplay(node))
    : outlineSource.outline.map(node => getOutlineNodeForDisplay(node));

  // Additional safety check for nodes
  if (!nodes || !Array.isArray(nodes)) {
    return false;
  }

  const documentNodeAnnotations = {};
  if (counterpoints) {
    let groundedTextData = counterpoints.map(n =>
      n.annotations.filter(nestedN => nestedN.selectedText.from?.outlineGroundedNode !== '')
    );
    if (groundedTextData.length) {
      nodes?.map(nodeData => {
        nodeData.groundedTextNodes?.forEach(n => {
          const nodeAnnotations = {
            counterpointAnnotationIDs: [],
            counterpoints: [],
            secondPart: [],
          };
          let nodeCounterpoints = counterpoints.reduce((nodeCPArr, cpData) => {
            return [
              ...nodeCPArr,
              {
                _id: cpData._id,
                author: cpData.author,
                team: cpData.team,
                annotations: cpData?.annotations
                  ?.filter(ann => !ann.refAnnotation._id)
                  ?.filter(ann => {
                    const annotationFrom = ann?.selectedText?.from?.nodeOrder;
                    const annotationTo = ann?.selectedText?.to?.nodeOrder;
                    const annotationNodeKey = ann?.selectedText?.from?.nodeKey;
                    const annotationNodeKeyTo = ann?.selectedText?.to?.nodeKey;
                    // long-form text-nodes nodeOrder property is formatted as number
                    if (typeof annotationFrom === 'number')
                      return (
                        annotationFrom >= n.nodeOrder &&
                        n.nodeOrder >= annotationTo &&
                        annotationNodeKey >= n.nodeKey &&
                        annotationNodeKeyTo <= n.nodeKey
                      );
                    // && (annotationNodeKey.split('-')[2] <= n.nodeKey.split('-')[2] && annotationNodeKeyTo.split('-')[2] >= n.nodeKey.split('-')[2])
                    // outline form text-nodes nodeOrder property is formatted as a string
                    // containing the outline-node ID tied to the text-node order
                    // {outline-node-id}-{text-node-order}
                    // ex. 0123456789abcdef-1
                    else if (typeof annotationFrom === 'string') {
                      const [annotationFromOutlineNodeId, annotationFromOrder] =
                        annotationFrom.split('-');
                      const [annotationToOutlineNodeId, annotationToOrder] =
                        annotationTo.split('-');
                      const [textNodeOutlineNodeId, textNodeOrder] = n.nodeOrder.split('-');

                      const textNodeOrderParsed = parseInt(textNodeOrder);

                      return (
                        textNodeOutlineNodeId === annotationFromOutlineNodeId &&
                        parseInt(annotationFromOrder) <= textNodeOrderParsed &&
                        textNodeOutlineNodeId === annotationToOutlineNodeId &&
                        textNodeOrderParsed <= parseInt(annotationToOrder)
                      );
                    }

                    // no other formats are accepted
                    else return false;
                  }),
              },
            ];
          }, []);
          //
          // flatten counterpoint comments
          nodeAnnotations.counterpointAnnotationIDs = nodeCounterpoints?.reduce(
            (annArr, cpData) => {
              return cpData?.annotations?.length
                ? [...annArr, ...cpData?.annotations?.map(ann => ann._id)]
                : [...annArr];
            },
            []
          );
          nodeAnnotations.secondPart = nodeCounterpoints?.reduce((annArr, cpData) => {
            return cpData?.annotations?.length
              ? [
                  ...annArr,
                  ...cpData?.annotations?.map(ann => ann?.selectedText?.from?.outlineGroundedNode),
                ]
              : [...annArr];
          }, []);
          nodeAnnotations.counterpoints = nodeCounterpoints.filter(
            cpData => cpData.annotations?.length
          );
          documentNodeAnnotations[n?.nodeKey] = nodeAnnotations;
        });
      });
    }
  }
  return documentNodeAnnotations;
};

//
// state
const state = {
  documentData: null,
  documents: {},
  documentLoaded: false,
  citations: {},
  publicLinkData: null,
  publicTargetNetwork: null,
  publicTargetAnnotationId: null,
  publicTargetURL: null,
  publicPostMessage: null,
  outlineEdit: false,
  articleParagraphs: {},
  acceptDecline: false,
  mobileStaticPage: false,
  acceptDeclineStatus: null,
  challengeUrl: false,
  challengeDetails: null,
  hedgingOnOff: false,
  reportView: false,
  observerStatus: false,
  analyticHideShow: [],
  listAnalyticAsync: {},
  runAnalyticCallDetails: false,
  secondInvites: false,
  // Cached computed data
  cachedAllData: null,
  cachedNodeAnnotations: null,
  cachedLLMNodeAnnotations: null,
};

//
// getters
const getters = {
  documentID: state => state.documentData?._id,
  documentStatus: state => state.documentData?.status,
  documentSelectedSource: state =>
    state.documentData?.sources?.find(
      source => getId(source) === getId(state.documentData.selectedSource)
    ) || {},
  documentOutlineSource: state =>
    state.documentData?.sources?.find(source => source.sourceType === 'Outline') || {},
  // TODO: Hedging, fallacies, concensus represent dynamic Analysis types that will eventually have
  // to be mapped to whatever is provided through LanguageAnalytics/REQUEST_ANALYTICS
  // Reasoning is currently supported

  // documentHedgingAnalytic: state =>
  //     state.documentData?.sources?.find(source => source.sourceType === "Hedging-Analytic" || source.sourceType === "Consensus" || source.sourceType === "Fallacies")
  //     || {},
  // documenFallaciesAnalytic: state =>
  //     state.documentData?.sources?.find(source => source.sourceType === "Fallacies")
  //     || {},
  // documenConsensusAnalytic: state =>
  //     state.documentData?.sources?.find(source => source.sourceType === "Consensus")
  //     || {},
  documenReasoningAnalytic: state =>
    state.documentData?.sources?.find(source => source.sourceType === 'Reasoning') || {},
  documentOutlineHumanSource: state =>
    state.documentData?.sources?.find(source => source.outlineType === 'Human-Outline') || {},
  documentOriginalSource: state =>
    state.documentData?.sources?.find(
      source => getId(source) === getId(state.documentData.originalSource)
    ) || {},
  hasNLPOutline: (state, getters) => !!getters.nlpOutline,
  getSelectedSource: () => document =>
    document?.sources?.find(source => getId(source) === getId(document.selectedSource)),
  getArticleSource: () => document =>
    document?.sources?.find(source => source.sourceType === DocumentType.ARTICLE),
  getOutlineSource: () => document =>
    document?.sources?.find(source => source.sourceType === DocumentType.OUTLINE),
  getCustomTextSource: () => document =>
    document?.sources?.find(source => source.sourceType === DocumentType.TEXT),
  permissionLevel: state => state.debateStatus?.permissionLevel,
  canEditOutline: (state, getters) =>
    [RoundStatus.EDITING_BASIS, RoundStatus.EDITING_NLP_OUTLINE].includes(getters.roundStatus),
  analyzeDocumnet: (state, getters) => [RoundStatus.ANALYZING_BASIS].includes(getters.roundStatus),
  documentMode: state => {
    const userDocumentData = state.userData?.documents?.find(
      d => getId(d.document) === getId(state.documentData)
    );
    return counterpointModesMap[userDocumentData?.permissionLevel];
  },

  documentSourceTextNodes: (state, getters) => {
    let selectedSource = getters.documentSelectedSource;
    switch (selectedSource.sourceType) {
      case DocumentType.ARTICLE:
      case DocumentType.TEXT:
        return selectedSource.nodes;
      case DocumentType.OUTLINE:
        return selectedSource.outline
          ?.map(node => getOutlineNodeForDisplay(node))
          ?.flatMap(outlineNode => getters.outlineInnerTextNodes(outlineNode));
    }
    // fallback to empty array
    return [];
  },
  documentSourceAnalytics: state => {
    const sources = state.documentData?.sources || [];
    const sourceAnalytics = {};

    sources.forEach(source => {
      if (
        source?.sourceType !== 'Article' &&
        source?.sourceType !== 'Outline' &&
        source?.sourceType !== 'Text'
      ) {
        sourceAnalytics[source.sourceType] = source;
      }
    });

    return sourceAnalytics;
  },

  documentAllData: (state, getters) => {
    if (state.cachedAllData) return state.cachedAllData;
    // Fallback: compute on-demand if cache is empty
    return buildAllData(getters);
  },
  documentNodeAnnotations: (state, getters) => {
    if (state.cachedNodeAnnotations) return state.cachedNodeAnnotations;
    // Fallback: compute on-demand if cache is empty
    return buildNodeAnnotations(state, getters);
  },
  documentNodeLLMAnnotations: (state, getters) => {
    if (state.cachedLLMNodeAnnotations !== null) return state.cachedLLMNodeAnnotations;
    // Fallback: compute on-demand if cache is empty
    return buildLLMNodeAnnotations(state, getters);
  },

  articleExcerpt: (state, getters) => span => {
    return getters.documentSourceTextNodes.filter(
      x => x.nodeOrder >= span.start && x.nodeOrder <= span.end
    );
  },

  getPublicLinkURL: () => publicLink => `${publicLink?.url}`,
  documentPulicLinks: (state, getters) => allowHide => {
    const outputLinks = state.documentData?.outputLinks || [];
    if (!outputLinks.length) return [];

    const hasMultipleLinks = outputLinks.length > 1;
    const teamID = getters.teamID;

    let options = [];

    if (hasMultipleLinks) {
      options.push({ label: 'Show All', value: 'all' });
    }

    options.push(
      ...outputLinks.map(link => ({
        label: link.team_id === teamID ? "My Side's Only" : "Counterpart's Only",
        value: link.team_id,
      }))
    );

    if (state.documentData.organization) {
      options.push({ label: 'Host Shareable Link', value: 'host' });
    }

    if (allowHide) {
      options.push({ label: 'Hide All', value: 'none' });
    }

    return options;
  },

  getLikesDislikesObj: () => (likes, dislikes) => {
    return {
      likes,
      dislikes,
    };
  },

  /**
   * Retrieve text span filled with given parameters containing a single text node
   */
  getSingleNodeSpan:
    (state, getters) =>
    (
      fullText,
      nodeKeyRoot,
      nodeOrder,
      nodeType,
      start_char,
      end_char,
      paragraphId,
      sentenceId,
      counterpointAnnotationIDs
    ) => {
      return {
        sentenceId,
        fullText,
        counterpointAnnotationIDs,
        nodes: [
          getters.getTextNode(
            fullText,
            nodeKeyRoot,
            nodeOrder,
            nodeType,
            start_char,
            end_char,
            paragraphId,
            sentenceId,
            counterpointAnnotationIDs.counterpointAnnotationIDs
          ),
        ],
      };
    },

  /**
   * Retrieve text node filled with given parameters
   */
  getTextNode:
    () =>
    (
      nodeContent,
      nodeKeyRoot,
      nodeOrder,
      nodeType,
      start_char,
      end_char,
      paragraphId,
      sentenceId,
      counterpointAnnotationIDs
    ) => {
      return {
        counterpointAnnotationIDs,
        nodeContent,
        nodeKey: `${nodeKeyRoot}${start_char}-${end_char}`,
        nodeOrder,
        nodeType,
        offsets: {
          start_char,
          end_char,
        },
        paragraphId,
        sentenceId,
      };
    },

  /** Validate if the host has started the debate and is no longer in Host Instructions mode */
  debateStarted(state, getters) {
    return (
      getters.documentStatus === DocumentStatus.PUBLISHED &&
      ![RoundStatus.HOST, RoundStatus.PUBLISHED].includes(getters.roundStatus)
    );
  },
};

//
// mutations
const mutations = {
  setCurrentDocumentID: (state, documentID) => {
    //
    // if changing to a different document
    if (state?.documentData?._id !== documentID) {
      state.documentData = { _id: documentID };
      // Clear cached computed data when switching documents
      state.cachedAllData = null;
      state.cachedNodeAnnotations = null;
      state.cachedLLMNodeAnnotations = null;
    }
  },
  setDocument: (state, document) => {
    state.documentData = document;
    // Clear cached computed data when document changes
    state.cachedAllData = null;
    state.cachedNodeAnnotations = null;
    state.cachedLLMNodeAnnotations = null;
  },
  setDocuments: (state, document) => (state.documents[document._id] = document),
  setDocumentFraming: (state, payload) => {
    Object.assign(state.documentData, payload);
  },
  setDocumentLoaded: (state, value) => (state.documentLoaded = value),
  setArticleParagraphs: (state, value) => (state.articleParagraphs = value),

  loadCitations: (state, citations) => {
    if (citations?.length) {
      state.citations = citations.reduce((obj, citation) => {
        return {
          ...obj,
          [citation.parentId]: obj[citation.parentId]
            ? [...obj[citation.parentId], citation]
            : [citation],
        };
      }, {});
    } else state.citations = {};
  },

  setPublicLinkId: (state, publicLinkId) => (state.publicLinkData = { _id: publicLinkId }),
  setPublicLinkData: (state, publicLinkData) => (state.publicLinkData = publicLinkData),
  setPublicTargetAnnotationId: (state, annotationId) =>
    (state.publicTargetAnnotationId = annotationId),
  setPublicTargetURL: (state, publicTargetURL) => (state.publicTargetURL = publicTargetURL),
  setPublicTargetNetwork: (state, network) => (state.publicTargetNetwork = network),
  setPublicPostMessage: (state, message) => (state.publicPostMessage = message),
  setAcceptDecline: (state, status) => (state.acceptDecline = status),
  setMobileStaticPage: (state, status) => (state.mobileStaticPage = status),
  setAcceptDeclineStatus: (state, status) => (state.acceptDeclineStatus = status),
  setChallengeUrl: (state, status) => (state.challengeUrl = status),
  setChallengeDetails: (state, status) => (state.challengeDetails = status),
  setHedgingOnOff: (state, status) => (state.hedgingOnOff = status),
  setAnalyticHideShow: (state, status) => (state.analyticHideShow = status),
  setRunAnalyticCallDetails: (state, status) => (state.runAnalyticCallDetails = status),
  setReportView: (state, status) => (state.reportView = status),
  setObserverStatus: (state, status) => (state.observerStatus = status),
  setListAnalyticAsync: (state, status) => (state.listAnalyticAsync = status),
  setComputedData: (state, { allData, nodeAnnotations, llmNodeAnnotations }) => {
    state.cachedAllData = allData;
    state.cachedNodeAnnotations = nodeAnnotations;
    state.cachedLLMNodeAnnotations = llmNodeAnnotations;
  },
  updateSelectedSource: (state, source) => {
    if (!state.documentData?.sources) {
      state.documentData.sources = [];
    }
    const index = state.documentData.sources.findIndex(s => getId(s) === getId(source));
    if (index !== -1) {
      // Direct assignment (works in Vue 2.7+)
      state.documentData.sources[index] = source;
    } else {
      // Fallback if the source wasn't found (or add it as first source)
      state.documentData.sources[0] = source;
    }
  },
  setSecondInvites: (state, value) => {
    state.secondInvites = value;
  },
  clearComputedData: state => {
    state.cachedAllData = null;
    state.cachedNodeAnnotations = null;
    state.cachedLLMNodeAnnotations = null;
  },
};

//
// actions
const actions = {
  setSelectedTextNode: ({ commit }, node) => {
    commit('setSelectedItem', { type: 'textNode', item: node });
    commit('setHoverOutlineNodes', node.outlineNodes);
  },
  createNewDocument: async (
    { state, commit, dispatch },
    { document, continueToNextScreen = true, success, failed }
  ) => {
    try {
      const result = await xhr.createDocument(document);
      if (result) {
        const documentId = result.document?.document;
        result.document._id = result.document?.document;
        // ***
        let addSourceData;

        switch (document.documentType) {
          case DocumentType.OUTLINE:
            addSourceData = {
              document_id: `${documentId}`,
              parent_document_source_id: null,
              sourceType: DocumentType.OUTLINE,
              content: null,
              analytical_content: null,
              publicationDate: null,
              sourceURL: null,
              nodes: null,
              outlineType: 'Human-Outline',
              outline: [],
            };
            break;

          case DocumentType.TEXT:
            addSourceData = {
              document_id: `${documentId}`,
              sourceType: DocumentType.TEXT,
              content: document ? document.documentText : '',
            };
            break;

          case DocumentType.ARTICLE:
            addSourceData = {
              document_id: `${documentId}`,
              sourceType: DocumentType.ARTICLE,
              sourceURL: document?.sourceURL || '',
              content: document?.articleText || '',
            };
            break;

          default:
            // Handle any other document types if needed
            break;
        }

        if (addSourceData) {
          let newSource = await xhr.addNewSourceDocument(addSourceData);
          if (newSource) {
            let sourceId = newSource.source_document_id;
            await dispatch('getParticularDocument', { sourceDocumentID: sourceId });
          }
        }
        const documentRes = await dispatch('getDocumentDetails', { documentId });
        // dispatch('addSessionLog', {
        //   location: 'document.js: createNewDocument: getDocumentDetails',
        //   initialStance: documentRes.initialStance,
        //   sourceType: documentRes.documentType,
        // })
        commit('setDocument', documentRes);
        commit('setDocuments', documentRes);

        commit('setUserData', {
          ...state.userData,
          documents: state.userData?.documents?.length
            ? [...state.userData.documents, result]
            : [result],
        });
        await success(result);
        if (continueToNextScreen) {
          if (document.discussion_type === 'curate') {
            router.push(this.$router.routesMap.CuratorDebate(documentId));
          } else {
            router.push(this.$router.routesMap.Debate(documentId));
          }
        }
      }
      return result;
    } catch (err) {
      console.log(err);
      failed(err);
      return null;
    }
  },

  updateDocument: async ({ state, commit }, { documentData, setData }) => {
    try {
      let res = await xhr.updateDocument(state.documentData._id, documentData);
      if (setData && state.basisEdited) {
        let { ...otherData } = res;
        commit('setDocumentFraming', otherData);
      } else {
        commit('setDocument', res);
        commit('setDocuments', res);
      }
      return res;
    } catch (err) {
      console.log(err);
    }
  },
  updateSourceDocument: async ({ state, commit }, { documentSourceData, documentSourceId }) => {
    try {
      let res = await xhr.updateSourceDocument(
        documentSourceId || getId(state.documentData.selectedSource),
        documentSourceData
      );
      if (res.sourceType === 'Outline') {
        res.outline = res.outline.map(node => getOutlineNodeForDisplay(node));
      }
      const docResult = res;
      commit('updateSelectedSource', res);
      commit('clearComputedData');
      return docResult;
    } catch (err) {
      console.log(err);
      return null;
    }
  },
  getParticularDocument: async ({ state, dispatch, commit }, { sourceDocumentID }) => {
    try {
      let res = await xhr.getParticularDocument(sourceDocumentID);
      commit('updateSelectedSource', res);
      commit('clearComputedData');
      if (getters.myLatestRound?.round > 3) {
        dispatch('setupDocument', state.documentData);
      }
      return res;
    } catch (err) {
      console.log(err);
    }
  },
  getSourceDocumentDetails: async (context, { sourceDocumentID }) => {
    try {
      let res = await xhr.getSourceDocumentDetails(sourceDocumentID);
      return res;
    } catch (err) {
      console.log(err);
    }
  },

  // New v2 source document actions
  getSourceDocumentsByDocument: async (context, { documentId }) => {
    try {
      let res = await xhr.getSourceDocumentsByDocument(documentId);
      return res;
    } catch (err) {
      console.log(err);
      return null;
    }
  },

  getSourceDocumentsByDebate: async (context, { debateId }) => {
    try {
      let res = await xhr.getSourceDocumentsByDebate(debateId);
      return res;
    } catch (err) {
      console.log(err);
      return null;
    }
  },

  getSourceDocumentById: async (context, { sourceDocumentId }) => {
    try {
      let res = await xhr.getSourceDocumentById(sourceDocumentId);
      return res;
    } catch (err) {
      console.log(err);
      return null;
    }
  },

  // Utility action to refresh source documents for current document
  refreshSourceDocuments: async ({ state, getters, commit }) => {
    try {
      const documentId = getters.documentID;
      const debateId = getters.debateID;

      if (!documentId) {
        console.warn('No document ID available for refreshing source documents');
        return null;
      }

      let sourceDocuments;

      if (debateId) {
        // Use debate_id endpoint for better query optimization
        sourceDocuments = await xhr.getSourceDocumentsByDebate(debateId);
      } else {
        // Fall back to document_id endpoint
        sourceDocuments = await xhr.getSourceDocumentsByDocument(documentId);
      }

      if (sourceDocuments && Array.isArray(sourceDocuments) && state.documentData?.sources) {
        // Update the existing sources with fresh data
        const updatedSources = state.documentData.sources.map(source => {
          const fullSourceData = sourceDocuments.find(sd => sd._id === source._id);
          if (fullSourceData) {
            return {
              ...source,
              nodes: fullSourceData.nodes,
              content: fullSourceData.content,
              outline: fullSourceData.outline,
              sourceType: fullSourceData.sourceType,
              outlineType: fullSourceData.outlineType || fullSourceData.outline_type,
              url: fullSourceData.url,
              sourceURL: fullSourceData.sourceURL || fullSourceData.source_url,
              analytical_content: fullSourceData.analytical_content,
              processing_status: fullSourceData.processing_status,
            };
          }
          return source;
        });

        // Update document data with refreshed sources
        commit('setDocument', {
          ...state.documentData,
          sources: updatedSources,
        });

        // Clear cached computed data to force recalculation
        commit('clearComputedData');

        return updatedSources;
      }

      return sourceDocuments;
    } catch (err) {
      console.log('Error refreshing source documents:', err);
      return null;
    }
  },
  getNodeFromText: async (context, { text }) => {
    try {
      let res = await xhr.getNodeFromText({ text: text });
      return res;
    } catch (err) {
      console.log(err);
    }
  },

  finalizeDraftedDocument: async ({ state }, { documentData, success, failed }) => {
    try {
      const result = await xhr.finalizeDraftedDocument(state.documentData._id, documentData);
      //
      // add team to user document
      const userDocument = state.userData.documents.find(
        userDoc => userDoc.document._id === result.document._id
      );
      userDocument.team = result.team;
      //
      // run success function
      success(result);
      //
      // redirect
      router.push(this.$router.routesMap.Debate(state.documentData._id));
    } catch (err) {
      console.log(err);
      failed(err);
    }
  },

  _canUseDocumentCache: ({ state, getters }, { documentId, isPublic }) => {
    return isPublic
      ? state.documents[documentId]?.status && !getters.canEditOutline
      : state.documents[documentId]?.status &&
          !getters.canEditOutline &&
          !getters.analyzeDocumnet &&
          state.runAnalyticCallDetails === false;
  },

  _fetchDocumentFromCache: ({ state, commit }, { documentId }) => {
    const res = state.documents[documentId];
    if (res.status === 'Published') {
      commit('setSecondInvites', true);
    }
    commit('setDocument', res);
    return res;
  },

  _fetchDocumentFromAPI: async ({ getters, commit }, { documentId, isPublic }) => {
    const apiCall = isPublic
      ? xhr.getPublicDocumentDetails(documentId)
      : xhr.getDocumentDetails(documentId, getters.publicLinkID);

    const sourceDocumentsCall = xhr.getSourceDocumentsByDocument(documentId);
    const [res, sourceDocuments] = await Promise.all([apiCall, sourceDocumentsCall]);

    if (!isPublic && res.sources) {
      // Map the source documents back to the response structure
      if (sourceDocuments && Array.isArray(sourceDocuments)) {
        res.sources = res.sources.map(source => {
          const fullSourceData = sourceDocuments.find(sd => sd._id === source._id);
          if (fullSourceData) {
            // Merge the full source data with existing source structure
            return {
              ...source,
              nodes: fullSourceData.nodes,
              content: fullSourceData.content,
              outline: fullSourceData.outline,
              sourceType: fullSourceData.sourceType,
              outlineType: fullSourceData.outlineType || fullSourceData.outline_type,
              url: fullSourceData.url,
              sourceURL: fullSourceData.sourceURL || fullSourceData.source_url,
              analytical_content: fullSourceData.analytical_content,
              processing_status: fullSourceData.processing_status,
            };
          }
          return source;
        });
      }
    }

    commit('setDocument', res);
    commit('setDocuments', res);
    if (res.status === 'Published') {
      commit('setSecondInvites', true);
    }
    if (!isPublic) {
      commit('setRunAnalyticCallDetails', false);
    }

    return res;
  },

  _fetchCitations: ({ getters, commit }, { isPublic }) => {
    // Guard against undefined documentID
    if (!getters.documentID) {
      console.warn('Cannot fetch citations: documentID is undefined');
      return;
    }

    const citationCall = isPublic
      ? xhr.getPublicCitations(getters.documentID)
      : xhr.getCitations(getters.documentID, getters.publicLinkID);

    citationCall
      .then(citations => {
        commit('loadCitations', citations || []);
      })
      .catch(error => {
        console.error('Error fetching citations:', error);
      });
  },

  _handleObserverVisitor: async ({ getters, commit }) => {
    const storedDeviceId = cookies.getCookie('deviceId');
    if (getters.teamID && !storedDeviceId) {
      try {
        let visitorData = {
          team_id: getters.teamID,
        };
        let data = await xhr.createVisitor(visitorData);
        commit('setVisitor', data.id);
        cookies.setCookie('deviceId', data.id);
      } catch (error) {
        if (error === 'user already exists') {
          // Handle the case where user already exists
          console.error('User already exists.');
        } else {
          // Handle other errors
          console.error('An error occurred:', error);
        }
      }
    }
  },

  _loadDebateStatus: async ({ state, dispatch }, { isPublic, documentId }) => {
    if (isPublic) {
      await dispatch('getPublicDebateStatus');
      await dispatch('retrievePublicDebateNextState');
    } else {
      const isObserver = JSON.parse(localStorage.getItem('isObserver'));
      if (state.observerStatus || (Array.isArray(isObserver) && isObserver.includes(documentId))) {
        await dispatch('getPublicDebateStatus');
        await dispatch('_handleObserverVisitor');
      } else {
        await dispatch('getDebateStatus');
      }
    }
  },

  _loadCounterpoints: async ({ state, dispatch, commit }) => {
    if (state.debateStatus) {
      const cpRes = await dispatch('loadCounterpoints', state.debateStatus);
      commit('setCounterpointLoaded', cpRes);
    }
  },

  _loadDebateDetails: async ({ dispatch }, { documentId, isPublic = false }) => {
    const canUseCache = await dispatch('_canUseDocumentCache', { documentId, isPublic });

    if (canUseCache) {
      return dispatch('_fetchDocumentFromCache', { documentId });
    } else {
      return dispatch('_fetchDocumentFromAPI', { documentId, isPublic });
    }
  },

  getDocumentDetails: async ({ dispatch }, { documentId, isPublic = false, successFn }) => {
    const [res] = await Promise.all([
      dispatch('_loadDebateDetails', { documentId, isPublic }),
      dispatch('_fetchCitations', { isPublic }),
      dispatch('_loadDebateStatus', { isPublic, documentId }),
    ]);

    await dispatch('_loadCounterpoints');
    await dispatch('retrieveDebateNextState');

    if (successFn) await successFn(res);
    return res;
  },

  // Backward compatibility wrapper for public document details
  getPublicDocumentDetails: async ({ dispatch }, { documentId, successFn }) => {
    return await dispatch('getDocumentDetails', { documentId, isPublic: true, successFn });
  },
  getPublicDocumentWithCounterpoints: async ({ getters, commit, dispatch }, successFn) => {
    try {
      const documentRes = await dispatch('getPublicDocumentDetails', {
        documentId: getters.documentID,
      });
      if (documentRes) {
        dispatch('setupDocument', documentRes);

        dispatch('reorderAnnotations');

        commit('setDocumentLoaded', true);
        if (successFn) successFn(documentRes);
        return documentRes;
      }
    } catch (err) {
      console.error(err);
    }
    return null;
  },
  getDocumentWithCounterpoints: async ({ getters, commit, dispatch }, successFn) => {
    try {
      const documentRes = await dispatch('getDocumentDetails', { documentId: getters.documentID });
      // await dispatch('getParticularDocument', { sourceDocumentID: getId(documentRes.selectedSource)});
      // dispatch('addSessionLog', {
      //   location: 'document.js: getDocumentWithCounterpoints: getDocumentDetails',
      //   initialStance: documentRes.initialStance,
      //   sourceType: documentRes.documentType,
      // })
      if (documentRes) {
        dispatch('setupDocument', documentRes);

        dispatch('reorderAnnotations');

        commit('setDocumentLoaded', true);
        if (successFn) successFn(documentRes);
        return documentRes;
      }
    } catch (err) {
      console.error(err);
    }
    return null;
  },
  getListAnalyticAsync: async ({ commit }) => {
    try {
      const data = await xhr.listAnalyticAsync();
      commit('setListAnalyticAsync', data);
    } catch (error) {
      console.error('error');
    }
  },
  // ***
  setupDocument: ({ state, getters, dispatch, commit }, documentRes) => {
    if (!documentRes) return;

    documentRes.nodesObj = toKeyedObj(getters.documentSourceTextNodes, 'nodeKey');
    const srcOutline = getters.documentOutlineSource?.outline;
    const outlineArr = Array.isArray(srcOutline?.outline) ? srcOutline.outline : srcOutline;
    const outlineData = outlineArr?.map(getOutlineNodeForDisplay);

    documentRes.outlineObj = outlineData?.map((n, idx) => ({
      index: idx,
      groundedTextNodes: toKeyedObj(n.groundedTextNodes, 'nodeKey'),
    }));
    documentRes.sources?.forEach(source => {
      if (source?.sourceType !== DocumentType.OUTLINE) return;
      const outlineSrc = Array.isArray(source.outline?.outline)
        ? source.outline.outline
        : source.outline;
      for (const node of outlineSrc || []) {
        dispatch('setupOutlineNode', { node: getOutlineNodeForDisplay(node) });
      }
    });

    // Compute and cache the heavy computational data
    // Only compute if we have the necessary data loaded
    if (state.documentData && getters.documentSourceTextNodes) {
      commit('setComputedData', {
        allData: buildAllData(getters),
        nodeAnnotations: buildNodeAnnotations(state, getters),
        llmNodeAnnotations: buildLLMNodeAnnotations(state, getters),
      });
    }

    const rounds = state.counterpointData?.rounds;
    if (rounds?.length && rounds[rounds.length - 1]?.round > 3) {
      // cache markup nodes & their order for fast slicing
      const markupNodes =
        getters.documentSourceTextNodes?.filter(n => n.counterpoints?.length) || [];
      const orderToContent = markupNodes.map(n => n.nodeContent);
      const orderToIndex = new Map(markupNodes.map((n, i) => [n.nodeOrder, i]));

      const rootTextCells = [];

      const resolveSortKey = annotation => {
        const nodeOrder = annotation?.selectedText?.from?.nodeOrder;
        if (typeof nodeOrder === 'number') {
          return nodeOrder;
        }
        if (typeof nodeOrder === 'string') {
          const parts = nodeOrder.split('-');
          const lastPart = parts[parts.length - 1];
          const parsed = Number(lastPart);
          if (Number.isFinite(parsed)) {
            return parsed;
          }
        }
        return Number.POSITIVE_INFINITY;
      };

      const getStandaloneRootText = annotation => {
        const candidates = [
          annotation?.selectedText?.text,
          annotation?.comment,
          annotation?.instruction,
          annotation?.title,
        ];
        const textCandidate = candidates.find(
          candidate => typeof candidate === 'string' && candidate.trim().length
        );
        return textCandidate || '(empty)';
      };

      const addRootTextCell = (annotation, counterpoint, rootText, sortKey) => {
        const insertionIndex = rootTextCells.length;
        rootTextCells.push({
          rootText,
          annotation,
          counterpoint,
          _sortKey: sortKey,
          _insertion: insertionIndex,
        });
      };

      for (const cp of rounds) {
        for (const ann of cp.annotations) {
          if (ann.refAnnotation?._id) continue;
          if (!ann?.selectedText?.from || !ann?.selectedText?.to) {
            console.info('Rendering annotation without selectedText bounds', ann?._id);
            addRootTextCell(ann, cp, getStandaloneRootText(ann), Number.POSITIVE_INFINITY);
            continue;
          }

          const fromIdx = orderToIndex.get(ann.selectedText.from.nodeOrder);
          const toIdx = orderToIndex.get(ann.selectedText.to.nodeOrder);

          if (fromIdx == null || toIdx == null) {
            console.info('Rendering annotation with unmapped selectedText bounds', ann?._id);
            addRootTextCell(ann, cp, getStandaloneRootText(ann), Number.POSITIVE_INFINITY);
            continue;
          }

          addRootTextCell(
            ann,
            cp,
            orderToContent.slice(fromIdx, toIdx + 1).join(''),
            resolveSortKey(ann)
          );
        }
      }

      rootTextCells.sort((a, b) => {
        if (a._sortKey === b._sortKey) {
          return a._insertion - b._insertion;
        }
        return a._sortKey - b._sortKey;
      });

      documentRes.compressedText = rootTextCells.map(({ _sortKey, _insertion, ...cell }) => cell);
    }
  },
  runAnalyticDocument: async ({ state }, { generateRunAnalytic, documentSourceId }) => {
    try {
      let res = await xhr.runAnalyticDocument(
        documentSourceId || getId(state.documentData.selectedSource),
        generateRunAnalytic
      );
      const docResult = res;

      return docResult;
    } catch (err) {
      console.log(err);
      return null;
    }
  },
  runAnalyticOutlineAsync: async ({ state }, { generateRunAnalytic, documentSourceId }) => {
    try {
      let res = await xhr.runAnalyticOutlineAsync(
        documentSourceId || getId(state.documentData.selectedSource),
        generateRunAnalytic
      );
      const docResult = res;

      return docResult;
    } catch (err) {
      console.log(err);
      return null;
    }
  },
  runLanguageAnalyticAsync: async ({ state }, { generateRunAnalytic, documentSourceId }) => {
    try {
      let res = await xhr.runLanguageAnalyticAsync(
        documentSourceId || getId(state.documentData.selectedSource),
        generateRunAnalytic
      );
      const docResult = res;

      return docResult;
    } catch (err) {
      console.log(err);
      return null;
    }
  },
  updateDocumentStatus: async ({ getters, commit, dispatch }, { status, sourceType }) => {
    let res = null;
    try {
      res = await xhr.updateDocumentStatus(getters.documentID, status, sourceType);
      commit('setDocument', res);
      dispatch('setupDocument', res);
      commit('setDocuments', res);
    } catch (err) {
      console.debug(err);
      alert('Error updating document status');
      return;
    }
    return res;
  },

  updateRoundStatus: async ({ getters }, { status, success, failed }) => {
    let res = null;
    const round = getters.myLatestRound;

    try {
      res = await xhr.updateRoundStatus(getters.documentID, round._id, status);
      round.status = status;
      if (success) success();
    } catch (err) {
      console.debug(err);
      if (failed) failed();
      alert('Error updating round status');
    }

    return res;
  },

  //
  // CITATIONS
  //
  getCitations: async ({ getters }, { success, failed }) => {
    try {
      // Guard against undefined documentID
      if (!getters.documentID) {
        console.warn('Cannot get citations: documentID is undefined');
        if (failed) failed(new Error('Document ID is undefined'));
        return null;
      }

      let res = await xhr.getCitations(getters.documentID).catch(err => failed && failed(err));
      if (success) success(res);
      return res;
    } catch (err) {
      console.error(err);
      if (failed) failed(err);
      return null;
    }
  },
  viewCitationText: async ({ getters }, { citationId, success, failed }) => {
    try {
      let res = await xhr
        .viewCitationText(getters.documentID, citationId)
        .catch(err => failed(err));
      if (success) success(res);
      return res;
    } catch (err) {
      console.error(err);
      if (failed) failed(err);
      return null;
    }
  },
  addCitation: async ({ getters }, { citationForm, success, failed }) => {
    try {
      let res = await xhr.addCitation(getters.documentID, citationForm).catch(err => failed(err));
      if (success) success(res);
      return res;
    } catch (err) {
      console.error(err);
      if (failed) failed(err);
      return null;
    }
  },
  updateCitation: async ({ getters }, { citationId, updatesData, success, failed }) => {
    try {
      let res = await xhr
        .updateCitation(getters.documentID, citationId, updatesData)
        .catch(err => failed(err));
      if (success) success(res);
      return res;
    } catch (err) {
      console.error(err);
      if (failed) failed(err);
      return null;
    }
  },
  deleteCitation: async ({ getters }, { citationId, success, failed }) => {
    try {
      let res = await xhr.deleteCitation(getters.documentID, citationId).catch(err => failed(err));
      if (success) success(res);
      return res;
    } catch (err) {
      console.error(err);
      if (failed) failed(err);
      return null;
    }
  },
  loadCitationDocument: async ({ getters }, { citation, documentData, success, failed }) => {
    try {
      let res = await xhr
        .loadCitationDocument(getters.debateID, citation._id, citation, documentData)
        .catch(err => failed(err));
      if (success) success(res);
      return res;
    } catch (err) {
      console.error(err);
      if (failed) failed(err);
      return null;
    }
  },
  setPublicInteractions: async ({ state, commit }, { value }) => {
    let result;
    //
    // get result based on input value
    switch (value) {
      case 'none':
        commit('setReportView', false);
        break;
      case 'host':
        result = [];
        break;
      // retrieve likes/dislikes data for all shareable links
      case 'all':
        commit('setReportView', true);
        result = state.counterpointData?.rounds
          .flatMap(round => round.annotations)
          .reduce((acc, annotation) => {
            const { teams } = annotation;

            teams.forEach(team => {
              const { total_likes, total_dislikes } = team;
              if (!acc[annotation._id]) {
                acc[annotation._id] = { likes: 0, dislikes: 0 };
              }
              acc[annotation._id].likes += total_likes;
              acc[annotation._id].dislikes += total_dislikes;
            });

            return acc;
          }, {});
        break;
      // retrieve likes/dislikes data for the selected shareable link
      default:
        commit('setReportView', true);
        result = state.counterpointData?.rounds
          .flatMap(round => round.annotations)
          .filter(annotation => {
            // Filter annotations to include only those that have the desired team ID
            return annotation?.teams?.some(team => team.id === value);
          })
          .reduce((acc, annotation) => {
            const { teams } = annotation;
            teams.forEach(team => {
              if (team.id === value) {
                const { total_likes, total_dislikes } = team;
                if (!acc[annotation._id]) {
                  acc[annotation._id] = { likes: 0, dislikes: 0 };
                }
                acc[annotation._id].likes += total_likes;
                acc[annotation._id].dislikes += total_dislikes;
              }
            });
            return acc;
          }, {});
        break;
    }
    commit('setAnnotationPublicLikesTotalsMap', result);
    commit('setShowAnnotationLikes', true);

    // const threadStartAnnotations = state.counterpointData.counterpointsFlat.filter(ann => !ann.refAnnotation);

    // // Calculate thread counts
    // const threadLikesCounts = threadStartAnnotations.reduce((obj, ann) => {
    //     // Get all annotations for the current thread
    //     const threadAnnotationsIds = getters.getRefAnnotationsIds(ann);

    //     // Initialize the total likes and dislikes for the current thread
    //     let totalLikes = 0;
    //     let totalDislikes = 0;

    //     // Sum likes/dislikes from thread
    //     threadAnnotationsIds.forEach(annId => {
    //         const annotationLikes = result[annId];

    //         // Sum likes/dislikes if data exists
    //         if (annotationLikes) {
    //             totalLikes += annotationLikes.likes || 0;
    //             totalDislikes += annotationLikes.dislikes || 0;
    //         }
    //     });

    //     // Update the threadLikesCounts object with the total likes and dislikes for the current thread annotation
    //     return {
    //         ...obj,
    //         [ann._id]: {
    //             likes: totalLikes,
    //             dislikes: totalDislikes
    //         }
    //     };
    // }, {});
    // commit('setThreadLikesCounts', threadLikesCounts);
  },

  copyLinkToClipboard: ({ getters }, link) => {
    navigator.clipboard.writeText(getters.getPublicLinkURL(link));
  },
};

//
// exports
export default {
  // namespaced: false,
  state,
  getters,
  actions,
  mutations,
};
